import {
  createPaymentResponseSchema,
  paymentLinkStatusResponseSchema,
  staffPaymentLinksListResponseSchema,
  type CreateAdhocPaymentLinkInput,
  type CreatePaymentResponse,
  type PaymentLinkStatusResponse,
  type PaymentProvider,
  type PaymentSettings,
  type StaffPaymentLinksListResponse,
} from '@unified-lk/api-contracts'
import type { AuthSession } from '../types/plans'

async function parseJson(response: Response): Promise<unknown> {
  return response.json()
}

async function authorizedRequest(
  path: string,
  session: AuthSession,
  init?: RequestInit,
): Promise<unknown> {
  const response = await fetch(path, {
    ...init,
    headers: {
      Accept: 'application/json',
      'Content-Type': 'application/json',
      Authorization: `Bearer ${session.accessToken}`,
      ...(init?.headers ?? {}),
    },
  })
  const json = await parseJson(response)
  if (!response.ok) {
    const msg =
      typeof json === 'object' && json !== null
        ? String(
            (json as Record<string, unknown>).error ??
              (json as Record<string, unknown>).message ??
              'API error',
          )
        : 'API error'
    throw new Error(msg)
  }
  return json
}

// ─── Staff: ad-hoc payment links ──────────────────────────────────────────────

export async function createAdhocPaymentLink(
  session: AuthSession,
  input: CreateAdhocPaymentLinkInput,
): Promise<CreatePaymentResponse> {
  const json = await authorizedRequest('/api/staff/payment-links', session, {
    method: 'POST',
    body: JSON.stringify(input),
  })
  return createPaymentResponseSchema.parse(json)
}

export async function listAdhocPaymentLinks(
  session: AuthSession,
  params?: { status?: string; limit?: number; offset?: number },
): Promise<StaffPaymentLinksListResponse> {
  const qs = new URLSearchParams()
  if (params?.status) qs.set('status', params.status)
  if (params?.limit !== undefined) qs.set('limit', String(params.limit))
  if (params?.offset !== undefined) qs.set('offset', String(params.offset))

  const query = qs.toString() ? '?' + qs.toString() : ''
  const json = await authorizedRequest(`/api/staff/payment-links${query}`, session)
  return staffPaymentLinksListResponseSchema.parse(json)
}

export async function listPlanPaymentLinks(
  session: AuthSession,
  planId: number,
  entityType: 'mis_plan' | 'constructor_plan' = 'mis_plan',
): Promise<StaffPaymentLinksListResponse> {
  const basePath =
    entityType === 'mis_plan' ? '/api/staff/plans' : '/api/staff/constructor-plans'
  const json = await authorizedRequest(`${basePath}/${planId}/payment-links`, session)
  return staffPaymentLinksListResponseSchema.parse(json)
}

export interface PaymentOverviewStats {
  total: number
  paid: number
  pending: number
  canceled: number
  total_paid_kopecks: number
}

export interface PaymentOverviewResponse {
  success: boolean
  stats: PaymentOverviewStats
  data: StaffPaymentLinksListResponse['data']
}

export async function getPaymentOverview(
  session: AuthSession,
  params?: { status?: string; source?: string; limit?: number; offset?: number },
): Promise<PaymentOverviewResponse> {
  const qs = new URLSearchParams()
  if (params?.status) qs.set('status', params.status)
  if (params?.source) qs.set('source', params.source)
  if (params?.limit !== undefined) qs.set('limit', String(params.limit))
  if (params?.offset !== undefined) qs.set('offset', String(params.offset))

  const query = qs.toString() ? '?' + qs.toString() : ''
  const json = await authorizedRequest(`/api/staff/payment-links/overview${query}`, session)
  return json as PaymentOverviewResponse
}

// ─── Public: create payment from share page ────────────────────────────────────

export async function createSharePayment(
  kind: 'mis-plans' | 'constructor-plans',
  token: string,
  input: {
    provider: PaymentProvider
    amount_kopecks: number
    yandex_split?: boolean
    description?: string
    patient_phone?: string
  },
): Promise<CreatePaymentResponse> {
  const response = await fetch(`/api/public/${kind}/${token}/payments`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(input),
  })
  const json = await parseJson(response)
  if (!response.ok) {
    const msg =
      typeof json === 'object' && json !== null
        ? String((json as Record<string, unknown>).error ?? 'Payment error')
        : 'Payment error'
    throw new Error(msg)
  }
  return createPaymentResponseSchema.parse(json)
}

export async function getPaymentLinkStatus(linkId: number): Promise<PaymentLinkStatusResponse> {
  const response = await fetch(`/api/public/payment-links/${linkId}/status`)
  const json = await parseJson(response)
  return paymentLinkStatusResponseSchema.parse(json)
}
